import cx from 'classnames';
import { useRef } from 'react';

import { Icon } from '@sb/design-system';
import type { IconKind } from '@sb/design-system';

export interface OptionsListOption {
  /** Stable id used as the row key and passed back via onSelect. */
  id: string;
  /** Icon shown on the left at 20×20 in a neutral grey. */
  iconKind: IconKind;
  /** Main label, 14/500/black. */
  title: string;
  /** Optional second line, 12/45% black. */
  subtitle?: string;
}

interface OptionsListProps {
  options: OptionsListOption[];
  /**
   * When set to a non-null id, that row fills brand-orange with white
   * text and the rest dim to 40%. The list also locks (further
   * clicks are no-ops) so the pick can't be retracted. Pass `null`
   * (or omit) to keep the list interactive.
   */
  selectedId?: string | null;
  /**
   * Temporarily blocks picks without locking the list: rows dim and
   * ignore clicks, and become interactive again when this flips back.
   * Unlike `selectedId`, this leaves no permanent selection behind.
   */
  disabled?: boolean;
  onSelect: (id: string) => void;
}

/**
 * Shared "choose one of these" card used by LandingHero and any
 * follow-up picker the agent emits (e.g. "What type of routine?").
 *
 * A single rounded-14 white container with hairline dividers
 * between rows; each row shows icon + title + optional subtitle.
 * Tapping a row fills it orange with white text and dims the
 * other rows to 40% opacity until the parent un-locks the picker.
 */
export function OptionsList({
  options,
  selectedId = null,
  disabled = false,
  onSelect,
}: OptionsListProps) {
  const isLocked = selectedId !== null;
  const isInert = isLocked || disabled;
  // Synchronous lock so two quick taps don't both pass the
  // state-driven `isLocked` check before the parent has re-rendered
  // with the new `selectedId`.
  const hasFiredRef = useRef(false);
  if (selectedId === null) hasFiredRef.current = false;

  const handleClick = (id: string) => {
    if (isInert || hasFiredRef.current) return;
    hasFiredRef.current = true;
    onSelect(id);
  };

  return (
    <div
      className="tw-rounded-14 tw-overflow-hidden"
      style={{
        // Themeable elevated surface + hairline divider border.
        background: 'var(--surface-primary-elevated)',
        border: '1px solid var(--divider-primary)',
      }}
    >
      {options.map((opt, idx) => {
        const isSelected = selectedId === opt.id;

        return (
          <div key={opt.id}>
            {idx > 0 && (
              <div
                // 1px divider + 14px inset don't map to spacing tokens
                // (height 1, side margin 14).
                style={{
                  margin: '0 14px',
                  height: 1,
                  background: 'var(--divider-primary)',
                }}
              />
            )}
            <button
              type="button"
              onClick={() => handleClick(opt.id)}
              disabled={isInert && !isSelected}
              className={cx(
                'tw-flex',
                'tw-items-center',
                'tw-gap-12',
                'tw-w-full',
                'tw-py-16',
                'tw-border-none',
                'tw-outline-none',
                'tw-text-left',
              )}
              style={{
                // Horizontal padding 14px isn't in scale; conditional
                // background / cursor / opacity stay inline. transition
                // covers both color + opacity changes.
                paddingLeft: 14,
                paddingRight: 14,
                background: isSelected ? 'var(--orange)' : 'transparent',
                cursor: isInert ? 'default' : 'pointer',
                opacity: isInert && !isSelected ? 0.4 : 1,
                transition: 'all 0.2s ease',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => {
                if (isInert) return;
                // eslint-disable-next-line no-param-reassign -- imperative hover background paired with onMouseLeave below
                e.currentTarget.style.background = 'var(--fill-primary)';
              }}
              onMouseLeave={(e) => {
                if (isInert) return;
                // eslint-disable-next-line no-param-reassign -- imperative hover background paired with onMouseLeave below
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Icon
                kind={opt.iconKind}
                className={cx(
                  'tw-w-20',
                  'tw-h-20',
                  'tw-shrink-0',
                  isSelected && 'tw-text-light',
                )}
                style={{
                  color: isSelected ? undefined : 'var(--label-tertiary)',
                }}
              />
              <div className="tw-flex-1 tw-min-w-0">
                <div
                  className={cx(
                    'tw-font-medium',
                    isSelected && 'tw-text-light',
                  )}
                  style={{
                    // 14px font + `-0.2` letter-spacing aren't in the
                    // typography scale.
                    fontSize: 14,
                    color: isSelected ? undefined : 'var(--label-primary)',
                    letterSpacing: -0.2,
                  }}
                >
                  {opt.title}
                </div>
                {opt.subtitle && (
                  <div
                    className={cx(
                      'tw-font-regular',
                      isSelected && 'tw-text-light',
                    )}
                    style={{
                      // 12px font + 1px margin + `-0.1` letter-spacing
                      // aren't in the scale. The selected subtitle dims to
                      // 70% via inline opacity — the design-system opacity
                      // scale has no `70` step (only 0/20/40/60/100).
                      fontSize: 12,
                      color: isSelected ? undefined : 'var(--label-tertiary)',
                      opacity: isSelected ? 0.7 : undefined,
                      marginTop: 1,
                      letterSpacing: -0.1,
                    }}
                  >
                    {opt.subtitle}
                  </div>
                )}
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}
